"use client";

import { useEffect, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { updateVenue, getVenueHidden } from "@/app/admin/actions";
import { CITIES } from "@/lib/constants";
import { Select } from "@/components/select";
import { useModalZIndex } from "@/lib/use-modal-z-index";
import { AppQueryProvider } from "@/app/providers";

export interface VenueEditInput {
  venue_id: number;
  name: string;
  slug?: string | null;
  city_slug?: string | null;
  address?: string | null;
  url?: string | null;
  hidden?: boolean;
}

interface VenueEditModalProps {
  venue: VenueEditInput;
  onClose: () => void;
}

export function VenueEditModal(props: VenueEditModalProps) {
  return <AppQueryProvider><VenueEditModalContent {...props} /></AppQueryProvider>;
}

function VenueEditModalContent({ venue, onClose }: VenueEditModalProps) {
  const zIndex = useModalZIndex();
  const [isPending, startTransition] = useTransition();
  const [name, setName] = useState(venue.name);
  const [citySlug, setCitySlug] = useState(venue.city_slug ?? "");
  const [address, setAddress] = useState(venue.address ?? "");
  const [url, setUrl] = useState(venue.url ?? "");
  const [hidden, setHidden] = useState(venue.hidden ?? false);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    if (venue.hidden === undefined) {
      getVenueHidden(venue.venue_id).then(setHidden);
    }
  }, [venue.hidden, venue.venue_id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSave = () => {
    if (!name.trim()) return;
    startTransition(async () => {
      await updateVenue(venue.venue_id, {
        name: name.trim(),
        address: address.trim() || null,
        city_slug: citySlug || null,
        url: url.trim() || null,
        hidden,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "venues"] });
      router.refresh();
      onClose();
    });
  };

  return (
    <div
      className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/80 px-4 py-12 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-md flex-col overflow-visible border border-neutral-800 bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -left-1 -top-1 h-3 w-3 border-l border-t border-acid" />
        <div className="absolute -right-1 -top-1 h-3 w-3 border-r border-t border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-neutral-800 p-5">
          <h2 className="font-mono text-sm font-bold tracking-wider text-white">
            EDIT VENUE
          </h2>
          <button
            onClick={onClose}
            className="shrink-0 text-neutral-500 transition-colors hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4 p-5">
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              NAME
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              CITY
            </label>
            <Select
              value={citySlug}
              onChange={setCitySlug}
              size="sm"
              options={[
                { label: "Select city...", value: "" },
                ...Object.values(CITIES).map((c) => ({
                  label: c.name,
                  value: c.slug,
                })),
              ]}
              className="min-w-0 flex-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              ADDRESS
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street address"
              className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              URL
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              HIDDEN
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={hidden}
                onChange={(e) => setHidden(e.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer appearance-none rounded border border-neutral-700 bg-transparent checked:border-acid checked:bg-acid"
              />
              <span className="font-mono text-[10px] text-neutral-500">
                {hidden ? "Hidden from public" : "Visible"}
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-neutral-800 p-5">
          <button
            onClick={onClose}
            className="cursor-pointer rounded border border-neutral-700 px-3 py-2.5 font-mono text-xs font-bold text-neutral-400 transition-colors hover:text-white"
          >
            CANCEL
          </button>
          <button
            onClick={handleSave}
            disabled={isPending || !name.trim()}
            className="flex-1 cursor-pointer rounded bg-acid py-2.5 font-mono text-sm font-bold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
